/**
 * Cart-drawer upsell: peek carousel, variant/property selects, add-to-cart.
 * Delegated so handlers survive Section Rendering morphs.
 * Loaded once from cart-drawer-section (not inside morphed HTML) so init always runs.
 */
(function () {
  if (window.__bcCupInit) return;
  window.__bcCupInit = true;

  function cartRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function replaceUpsell(sectionHtml) {
    if (!sectionHtml) return;
    const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const next = doc.querySelector('.bc-cup-wrap, [data-bc-cup]');
    const live = document.querySelector('#cart-drawer .bc-cup-wrap, #cart-drawer [data-bc-cup]');
    if (!next || !live) return;
    live.replaceWith(next.cloneNode(true));
  }

  function resetCarousels() {
    document.querySelectorAll('#cart-drawer [data-bc-cup-carousel]').forEach((carousel) => {
      carousel.dataset.index = '0';
      const viewport = getViewport(carousel);
      if (viewport) viewport.scrollTo({ left: 0, behavior: 'auto' });
    });
  }

  function collectSectionIds() {
    const ids = [];
    document.querySelectorAll('cart-items-component').forEach((el) => {
      const id = el.dataset?.sectionId;
      if (id && !ids.includes(id)) ids.push(id);
    });
    return ids;
  }

  function publishCartUpdate(cart, sections) {
    return import('@shopify/events')
      .then((mod) => {
        const { CartLinesUpdateEvent } = mod;
        const deferred = CartLinesUpdateEvent.createPromise();
        document.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'cart',
            lines: [],
            promise: deferred.promise,
          })
        );
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            source: 'bc-cart-upsell',
            itemCount: cart?.item_count,
            items: cart?.items,
            sections: sections || {},
          },
        });
      })
      .catch(() => {
        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: { source: 'bc-cart-upsell', cart, sections },
          })
        );
      });
  }

  function openCartDrawer() {
    const drawer = document.querySelector('theme-drawer#cart-drawer');
    if (drawer?.isOpen) return;
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      return;
    }
    document.querySelector('[aria-controls="cart-drawer"]')?.click();
  }

  function parseVariants(slide) {
    const node = slide.querySelector('[data-bc-cup-variants]');
    if (!node) return [];
    try {
      return JSON.parse(node.textContent || '[]');
    } catch (e) {
      return [];
    }
  }

  function normalizeOption(value) {
    return String(value ?? '').trim();
  }

  function selectedOptions(slide) {
    return [...slide.querySelectorAll('[data-bc-cup-option]')].map((el) => normalizeOption(el.value));
  }

  function optionsComplete(slide) {
    const selects = [...slide.querySelectorAll('[data-bc-cup-option]')];
    if (!selects.length) return true;
    return selects.every((el) => Boolean(normalizeOption(el.value)));
  }

  function findVariant(variants, options) {
    if (!variants.length) return null;
    if (!options.length) return variants.find((v) => v.available) || variants[0];
    if (options.some((opt) => !opt)) return null;
    return (
      variants.find(
        (v) =>
          Array.isArray(v.options) &&
          v.options.length === options.length &&
          v.options.every((opt, i) => normalizeOption(opt) === options[i])
      ) || null
    );
  }

  function renderPrice(el, variant) {
    if (!el || !variant) return;
    if (variant.compare) {
      el.innerHTML = `<s>${variant.compare}</s> <strong>${variant.price}</strong>`;
    } else {
      el.innerHTML = `<strong>${variant.price}</strong>`;
    }
  }

  function getSlideError(slide) {
    return slide.querySelector('[data-bc-cup-slide-error]');
  }

  function showSlideError(slide, message) {
    const error = getSlideError(slide);
    if (!(error instanceof HTMLElement)) return;
    error.textContent = message;
    error.hidden = false;
  }

  function clearSlideError(slide) {
    const error = getSlideError(slide);
    if (error instanceof HTMLElement) error.hidden = true;
  }

  function showSelectError(select) {
    if (!(select instanceof HTMLElement)) return;
    select.classList.add('bc-cup__select--error');
    select.setAttribute('aria-invalid', 'true');
    const error = select.parentElement?.querySelector('.bc-cup__select-error');
    if (error instanceof HTMLElement) error.hidden = false;
  }

  function clearSelectError(select) {
    if (!(select instanceof HTMLElement)) return;
    select.classList.remove('bc-cup__select--error');
    select.removeAttribute('aria-invalid');
    const error = select.parentElement?.querySelector('.bc-cup__select-error');
    if (error instanceof HTMLElement) error.hidden = true;
  }

  function markEmptySelects(slide, selector) {
    let ok = true;
    slide.querySelectorAll(selector).forEach((select) => {
      if (normalizeOption(select.value)) {
        clearSelectError(select);
        return;
      }
      ok = false;
      showSelectError(select);
    });
    return ok;
  }

  function clearAllSelectErrors(slide) {
    slide.querySelectorAll('[data-bc-cup-option], [data-bc-cup-property]').forEach(clearSelectError);
  }

  function syncSlideVariant(slide) {
    const variants = parseVariants(slide);
    const options = selectedOptions(slide);
    const variant = optionsComplete(slide) ? findVariant(variants, options) : null;
    const addBtn = slide.querySelector('.bc-cup__add');
    const priceEl = slide.querySelector('[data-bc-cup-price]');
    if (!addBtn) return variant;

    if (!variant) {
      addBtn.setAttribute('data-bc-cup-add', '');
      addBtn.removeAttribute('aria-disabled');
      if (addBtn.dataset.bcBusy !== '1') {
        addBtn.textContent = addBtn.dataset.ctaLabel || 'Add';
      }
      return null;
    }

    addBtn.setAttribute('data-bc-cup-add', String(variant.id));
    if (addBtn.dataset.bcBusy !== '1') {
      addBtn.textContent = variant.available ? addBtn.dataset.ctaLabel || 'Add' : 'Sold out';
    }
    renderPrice(priceEl, variant);
    return variant;
  }

  function collectProperties(slide) {
    const props = {};
    slide.querySelectorAll('[data-bc-cup-property]').forEach((select) => {
      const key = select.getAttribute('data-bc-cup-property');
      if (!key) return;
      props[key] = normalizeOption(select.value);
    });
    return props;
  }

  function getViewport(carousel) {
    return carousel.querySelector('.bc-cup__viewport');
  }

  function goToSlide(carousel, nextIndex) {
    const viewport = getViewport(carousel);
    const slides = [...carousel.querySelectorAll('.bc-cup__slide')];
    if (!viewport || !slides.length) return;
    const max = slides.length - 1;
    const index = Math.max(0, Math.min(max, nextIndex));
    carousel.dataset.index = String(index);

    const maxScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    let left = 0;
    if (index >= max) {
      left = maxScroll;
    } else {
      const slide = slides[index];
      left = Math.min(maxScroll, slide.offsetLeft);
    }
    viewport.scrollTo({ left, behavior: 'smooth' });
  }

  function syncIndexFromScroll(carousel) {
    const viewport = getViewport(carousel);
    const slides = [...carousel.querySelectorAll('.bc-cup__slide')];
    if (!viewport || !slides.length) return;
    const maxScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    if (maxScroll > 0 && viewport.scrollLeft >= maxScroll - 2) {
      carousel.dataset.index = String(slides.length - 1);
      return;
    }
    const mid = viewport.scrollLeft + viewport.clientWidth * 0.35;
    let best = 0;
    for (let i = 0; i < slides.length; i++) {
      if (slides[i].offsetLeft <= mid) best = i;
    }
    carousel.dataset.index = String(best);
  }

  function handleSelectChange(event) {
    const option = event.target.closest?.('[data-bc-cup-option]');
    const property = event.target.closest?.('[data-bc-cup-property]');
    const select = option || property;
    if (!select) return;
    const slide = select.closest('[data-bc-cup-slide]');
    if (!slide) return;

    clearSlideError(slide);
    if (property) clearSelectError(property);
    if (option) {
      clearSelectError(option);
      syncSlideVariant(slide);
    }
  }

  document.addEventListener('change', handleSelectChange);
  document.addEventListener('input', handleSelectChange);

  let sizeState = null;
  let sizeCloseTimer = 0;

  function optionGroups(slide) {
    return [...slide.querySelectorAll('[data-bc-cup-option]')].map((select) => ({
      name: select.getAttribute('aria-label') || 'Size',
      values: [...select.options].map((opt) => normalizeOption(opt.value)).filter(Boolean),
    }));
  }

  function propertyGroups(slide) {
    return [...slide.querySelectorAll('[data-bc-cup-property]')].map((select) => ({
      name: select.getAttribute('aria-label') || 'Option',
      values: [...select.options].map((opt) => normalizeOption(opt.value)).filter(Boolean),
    }));
  }

  function needsSizePicker(slide) {
    return (
      optionGroups(slide).some((group) => group.values.length > 1) ||
      propertyGroups(slide).some((group) => group.values.length > 1)
    );
  }

  function ensureSizeDialog() {
    const closeButton =
      '<button type="button" class="bc-cup-size__close" data-bc-cup-size-close aria-label="Close">' +
      '<svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">' +
      '<path d="M3.2 3.2l9.6 9.6M12.8 3.2l-9.6 9.6" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
      '</svg></button>';

    let root = document.querySelector('[data-bc-cup-size]');
    if (root) {
      const panel = root.querySelector('.bc-cup-size__panel');
      if (panel && !panel.querySelector('.bc-cup-size__close')) {
        panel.insertAdjacentHTML('afterbegin', closeButton);
      }
      return root;
    }

    root = document.createElement('div');
    root.className = 'bc-cup-size';
    root.setAttribute('data-bc-cup-size', '');
    root.hidden = true;
    root.innerHTML =
      '<div class="bc-cup-size__backdrop" data-bc-cup-size-close></div>' +
      '<div class="bc-cup-size__panel" role="dialog" aria-modal="true" aria-labelledby="bc-cup-size-title" tabindex="-1">' +
      closeButton +
      '<div class="bc-cup-size__grabber" aria-hidden="true"></div>' +
      '<p class="bc-cup-size__title" id="bc-cup-size-title"></p>' +
      '<div data-bc-cup-size-options></div>' +
      '<p class="bc-cup-size__error" data-bc-cup-size-error hidden></p>' +
      '<button type="button" class="bc-cup-size__submit" data-bc-cup-size-submit disabled>Add to Bag</button>' +
      '</div>';
    document.body.appendChild(root);
    return root;
  }

  function optionValueAvailable(variants, optionIndex, value, selected) {
    return variants.some((variant) => {
      if (!variant.available || !Array.isArray(variant.options)) return false;
      if (normalizeOption(variant.options[optionIndex]) !== value) return false;
      return selected.every((choice, index) => {
        if (index === optionIndex || !choice) return true;
        return normalizeOption(variant.options[index]) === choice;
      });
    });
  }

  function chosenVariant() {
    if (!sizeState) return null;
    const { variants, optionGroups: groups, options } = sizeState;
    if (!groups.length) return variants.find((variant) => variant.available) || variants[0] || null;
    if (options.some((value) => !value)) return null;
    return findVariant(variants, options);
  }

  function renderSizeOptions() {
    if (!sizeState) return;
    const root = ensureSizeDialog();
    const mount = root.querySelector('[data-bc-cup-size-options]');
    const submit = root.querySelector('[data-bc-cup-size-submit]');
    if (!mount || !submit) return;

    const { optionGroups: groups, propertyGroups: props, options, properties, variants } = sizeState;
    mount.replaceChildren();

    const paint = (items, selected, kind) => {
      items.forEach((group, groupIndex) => {
        const block = document.createElement('div');
        block.className = 'bc-cup-size__group';
        const label = document.createElement('p');
        label.className = 'bc-cup-size__label';
        label.textContent = group.name;
        const pills = document.createElement('div');
        pills.className = 'bc-cup-size__pills';
        pills.setAttribute('role', 'listbox');
        pills.setAttribute('aria-label', group.name);

        group.values.forEach((value) => {
          const pill = document.createElement('button');
          pill.type = 'button';
          pill.className = 'bc-cup-size__pill';
          pill.textContent = value;
          pill.setAttribute('role', 'option');
          pill.dataset.kind = kind;
          pill.dataset.group = String(groupIndex);
          pill.dataset.value = value;
          const pressed = selected[groupIndex] === value;
          pill.setAttribute('aria-selected', pressed ? 'true' : 'false');
          if (pressed) pill.classList.add('is-selected');
          if (kind === 'option' && !optionValueAvailable(variants, groupIndex, value, options)) {
            pill.disabled = true;
          }
          pills.appendChild(pill);
        });

        block.append(label, pills);
        mount.appendChild(block);
      });
    };

    paint(groups, options, 'option');
    paint(props, properties, 'property');

    const variant = chosenVariant();
    const ready =
      options.every(Boolean) &&
      properties.every(Boolean) &&
      variant &&
      variant.available;
    submit.disabled = !ready;
    submit.textContent = 'Add to Bag';
  }

  function openSizePicker(slide, addBtn) {
    const root = ensureSizeDialog();
    const title = slide.querySelector('.bc-cup__title')?.textContent?.trim() || 'Select a size';
    const titleEl = root.querySelector('.bc-cup-size__title');
    const error = root.querySelector('[data-bc-cup-size-error]');
    if (titleEl) titleEl.textContent = title;
    if (error) error.hidden = true;

    const groups = optionGroups(slide);
    const props = propertyGroups(slide);
    sizeState = {
      slide,
      addBtn,
      variants: parseVariants(slide),
      optionGroups: groups,
      propertyGroups: props,
      options: groups.map(() => ''),
      properties: props.map(() => ''),
    };
    renderSizeOptions();
    window.clearTimeout(sizeCloseTimer);
    root.classList.remove('is-open');
    root.hidden = false;
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        root.classList.add('is-open');
        root.querySelector('.bc-cup-size__panel')?.focus();
      });
    });
  }

  function closeSizePicker(options = {}) {
    const root = document.querySelector('[data-bc-cup-size]');
    const returnFocus = sizeState?.addBtn;
    sizeState = null;
    window.clearTimeout(sizeCloseTimer);

    const finish = () => {
      if (root) {
        root.hidden = true;
        root.classList.remove('is-open');
      }
      if (options.focus === false) return;
      if (returnFocus instanceof HTMLElement && document.contains(returnFocus)) returnFocus.focus();
    };

    if (!root || root.hidden || options.focus === false) {
      finish();
      return;
    }

    root.classList.remove('is-open');
    sizeCloseTimer = window.setTimeout(finish, 220);
  }

  function showSizeError(message) {
    const error = document.querySelector('[data-bc-cup-size-error]');
    if (!(error instanceof HTMLElement)) return;
    error.textContent = message;
    error.hidden = false;
  }

  function commitAdd(slide, addBtn, variant, statusBtn) {
    const variantId = Number(variant?.id);
    if (!variantId) {
      showSlideError(slide, 'Could not find this variant. Please try again.');
      return;
    }

    const properties = collectProperties(slide);
    const label = addBtn.dataset.ctaLabel || 'Add';
    const status = statusBtn || addBtn;
    const usingModal = status !== addBtn;
    addBtn.dataset.bcBusy = '1';
    status.disabled = true;
    if (!usingModal) addBtn.disabled = true;
    status.textContent = 'Adding…';

    const sectionIds = collectSectionIds();
    const root = cartRoot();
    const item = { id: variantId, quantity: 1 };
    if (Object.keys(properties).length) item.properties = properties;

    fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        items: [item],
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      }),
    })
      .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
      .then((res) => {
        if (!res.ok || res.data.status) {
          throw new Error(res.data.description || res.data.message || 'Could not add to cart');
        }
        const sections = res.data.sections || {};
        closeSizePicker({ focus: false });
        return fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })
          .then((response) => response.json().then((cart) => publishCartUpdate(cart, sections)))
          .then(() => {
            const sectionId = sectionIds[0];
            if (sectionId) replaceUpsell(sections[sectionId]);
            resetCarousels();
          });
      })
      .then(() => {
        if (document.contains(addBtn)) {
          addBtn.textContent = label;
          addBtn.disabled = false;
          addBtn.dataset.bcBusy = '0';
        }
        clearSlideError(slide);
        openCartDrawer();
      })
      .catch((err) => {
        const message = err?.message || 'Could not add to cart. Please try again.';
        if (document.querySelector('[data-bc-cup-size]:not([hidden])')) {
          showSizeError(message);
          if (status !== addBtn) {
            status.disabled = false;
            status.textContent = 'Add to Bag';
          }
        } else {
          showSlideError(slide, message);
        }
        if (document.contains(addBtn)) {
          addBtn.textContent = label;
          addBtn.disabled = false;
          addBtn.dataset.bcBusy = '0';
        }
        if (sizeState) sizeState.busy = false;
      });
  }

  function confirmSizePicker() {
    if (!sizeState || sizeState.busy) return;
    const { slide, addBtn, options, properties } = sizeState;
    const error = document.querySelector('[data-bc-cup-size-error]');
    if (error) error.hidden = true;
    if (options.some((value) => !value) || properties.some((value) => !value)) return;

    const variant = chosenVariant();
    if (!variant || !variant.available) {
      showSizeError('This size is sold out. Please choose another.');
      return;
    }

    slide.querySelectorAll('[data-bc-cup-option]').forEach((select, index) => {
      select.value = options[index] || '';
    });
    slide.querySelectorAll('[data-bc-cup-property]').forEach((select, index) => {
      select.value = properties[index] || '';
    });
    syncSlideVariant(slide);
    sizeState.busy = true;

    const submit = document.querySelector('[data-bc-cup-size-submit]');
    commitAdd(slide, addBtn, variant, submit);
  }

  document.addEventListener('click', (event) => {
    const root = document.querySelector('[data-bc-cup-size]');
    if (!root || root.hidden || !sizeState) return;

    if (event.target.closest?.('[data-bc-cup-size-close]')) {
      event.preventDefault();
      closeSizePicker();
      return;
    }

    const pill = event.target.closest?.('.bc-cup-size__pill');
    if (pill && !pill.disabled && !sizeState.busy) {
      event.preventDefault();
      const index = Number(pill.dataset.group);
      if (pill.dataset.kind === 'property') sizeState.properties[index] = pill.dataset.value || '';
      else sizeState.options[index] = pill.dataset.value || '';
      const kind = pill.dataset.kind;
      const value = pill.dataset.value;
      renderSizeOptions();
      const next = [...document.querySelectorAll('.bc-cup-size__pill')].find(
        (button) => button.dataset.kind === kind && button.dataset.group === String(index) && button.dataset.value === value
      );
      next?.focus();
      return;
    }

    if (event.target.closest?.('[data-bc-cup-size-submit]')) {
      event.preventDefault();
      confirmSizePicker();
    }
  });

  document.addEventListener(
    'keydown',
    (event) => {
      const root = document.querySelector('[data-bc-cup-size]');
      if (!root || root.hidden) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        closeSizePicker();
      }
    },
    true
  );

  function handleAddClick(event) {
    const addBtn = event.target.closest?.('.bc-cup__add');
    if (!addBtn || addBtn.dataset.bcBusy === '1') return;

    const slide = addBtn.closest('[data-bc-cup-slide]');
    if (!slide) return;

    event.preventDefault();
    event.stopPropagation();

    if (!addBtn.dataset.ctaLabel) {
      addBtn.dataset.ctaLabel = addBtn.textContent.trim() || 'Add';
    }

    if (needsSizePicker(slide)) {
      openSizePicker(slide, addBtn);
      return;
    }

    clearSlideError(slide);
    clearAllSelectErrors(slide);

    const optionsOk = markEmptySelects(slide, '[data-bc-cup-option]');
    const propertiesOk = markEmptySelects(slide, '[data-bc-cup-property]');
    if (!optionsOk || !propertiesOk) return;

    const variant = syncSlideVariant(slide);
    if (!variant) {
      showSlideError(slide, 'This combination is unavailable. Please choose different options.');
      return;
    }
    if (!variant.available) {
      showSlideError(slide, 'This variant is sold out.');
      addBtn.textContent = 'Sold out';
      return;
    }

    commitAdd(slide, addBtn, variant);
  }

  document.addEventListener('click', handleAddClick, true);

  document.addEventListener('click', (event) => {
    const nav = event.target.closest?.('[data-bc-cup-nav]');
    if (!nav) return;
    const carousel = nav.closest('[data-bc-cup-carousel]');
    if (!carousel) return;
    event.preventDefault();
    const current = Number(carousel.dataset.index || 0);
    const dir = nav.getAttribute('data-bc-cup-nav') === 'next' ? 1 : -1;
    const slides = carousel.querySelectorAll('.bc-cup__slide');
    const max = Math.max(0, slides.length - 1);
    let next = current + dir;
    if (next > max) next = 0;
    if (next < 0) next = max;
    goToSlide(carousel, next);
  });

  document.addEventListener(
    'scroll',
    (event) => {
      const viewport = event.target?.closest?.('.bc-cup__viewport');
      if (!viewport) return;
      const carousel = viewport.closest('[data-bc-cup-carousel]');
      if (carousel) syncIndexFromScroll(carousel);
    },
    { passive: true, capture: true }
  );

  window.addEventListener(
    'resize',
    () => {
      document.querySelectorAll('[data-bc-cup-carousel]').forEach((carousel) => {
        const viewport = getViewport(carousel);
        if (!viewport) return;
        const index = Number(carousel.dataset.index || 0);
        const slides = [...carousel.querySelectorAll('.bc-cup__slide')];
        const max = Math.max(0, slides.length - 1);
        const maxScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
        const left =
          index >= max ? maxScroll : Math.min(maxScroll, slides[index]?.offsetLeft || 0);
        viewport.scrollTo({ left, behavior: 'auto' });
      });
    },
    { passive: true }
  );
})();
